import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve a brand's logo to a fully-qualified URL usable by `<img src>`.
 * Falls back to the legacy base64 data-URL if the record hasn't been
 * migrated to Object Storage yet.
 */
export function resolveBrandLogo(brand) {
  if (!brand) return null;
  const url = brand.logo_url;
  if (url) return url.startsWith("http") ? url : `${API_BASE}${url}`;
  return brand.logo_data_url || null;
}

/**
 * BrandContext — SLUG-IS-MASTER white-label theming.
 *
 * Contract (iter83 rewrite):
 *   1. The active brand is driven EXCLUSIVELY by the current URL slug.
 *      No sessionStorage. No localStorage. No user-object fallback.
 *      If the URL has a slug, we fetch that brand. If it doesn't, brand
 *      is `null` (neutral Dugsy).
 *   2. When the URL slug changes, the previous brand is CLEARED
 *      SYNCHRONOUSLY (before the fetch starts) so no cross-pollution is
 *      ever visible between tenants during navigation.
 *   3. `setBrandBySlug` and `clearBrand` remain as public escape hatches
 *      for imperative flows (SluggedLoginPage, ClientLandingPage,
 *      RegisterPage) but they NEVER cache anything client-side beyond
 *      the in-memory `brand` state.
 *
 * Applies to :root when `brand` changes:
 *   - CSS variable --brand-primary
 *   - CSS variable --brand-primary-hover (12% darker)
 *   - CSS variable --brand-bg-tint (5% wash)
 */

const BrandCtx = createContext({
  brand: null,
  bootstrapped: false,
  setBrandBySlug: () => {},
  clearBrand: () => {},
});

const DEFAULT_PRIMARY = "#0038FF";

/**
 * Top-level path segments that are NOT tenant slugs. Anything else at
 * the bare `/:slug` position is treated as a tenant slug and resolved
 * to its brand via the same API path as `/c/:slug/*`. Kept in lock-step
 * with the top-level routes declared in `App.js`.
 */
const RESERVED_TOP_LEVEL = new Set([
  "",
  "login", "register", "reset", "terms", "privacy", "cookies",
  "unsubscribe", "business", "redeem", "c", "client",
  "editor", "settings", "admin",
  "leaderboard", "leagues", "matches",
  "champions", "seasons-archive", "payments",
]);

function darkenHex(hex, pct = 12) {
  let v = (hex || "").replace("#", "");
  if (v.length === 3) v = v.split("").map((c) => c + c).join("");
  if (v.length !== 6) return DEFAULT_PRIMARY;
  const num = parseInt(v, 16);
  const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - pct / 100)));
  const g = Math.max(0, Math.round(((num >> 8) & 0xff) * (1 - pct / 100)));
  const b = Math.max(0, Math.round((num & 0xff) * (1 - pct / 100)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0").toUpperCase()}`;
}

function hexToRgba(hex, alpha = 0.06) {
  let v = (hex || "").replace("#", "");
  if (v.length === 3) v = v.split("").map((c) => c + c).join("");
  if (v.length !== 6) return `rgba(0, 56, 255, ${alpha})`;
  const num = parseInt(v, 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Extract the tenant slug from the current pathname. Recognises both:
 *   - `/c/:slug/*` (client-scoped area — back office, leaderboard, etc.)
 *   - Bare `/:slug` (branded login landing via SluggedLoginPage)
 * Returns `null` for any reserved top-level path or non-slug shape.
 */
function extractSlugFromPath(pathname) {
  const scoped = pathname.match(/^\/c\/([^/]+)(?:\/|$)/);
  if (scoped) return scoped[1];
  const bare = pathname.match(/^\/([^/]+)\/?$/);
  if (!bare) return null;
  const seg = bare[1];
  if (RESERVED_TOP_LEVEL.has(seg)) return null;
  if (!/^[a-z0-9-]+$/.test(seg)) return null;
  return seg;
}

export function BrandProvider({ children }) {
  const { pathname } = useLocation();
  const [brand, setBrand] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  // Track the slug the currently-shown `brand` was fetched for. Guards
  // against a stale response landing after the URL has moved on (e.g.
  // fast tab-switching between /c/pub-a and /c/pub-b).
  const activeSlugRef = useRef(null);

  const urlSlug = useMemo(() => extractSlugFromPath(pathname), [pathname]);

  const fetchBrand = useCallback(async (slug) => {
    if (!slug) return null;
    try {
      const { data } = await api.get(`/pubs/by-slug/${encodeURIComponent(slug)}`);
      return data;
    } catch {
      return null;
    }
  }, []);

  /**
   * SLUG-IS-MASTER effect: fires every time the URL slug changes.
   *
   * STEP 1 (synchronous): clear the previous brand IMMEDIATELY so the
   *        header/logo/colours don't leak from tenant A onto tenant B
   *        during the in-flight fetch. This is the critical anti-cross-
   *        pollution guarantee.
   * STEP 2 (asynchronous): fetch the new slug's brand. Late responses
   *        for a slug that's no longer active are discarded via
   *        `activeSlugRef`.
   */
  useEffect(() => {
    // STEP 1 — synchronous clear. Even when we're about to load a new
    // brand, we go through `null` first so no frame ever renders the
    // previous tenant's colours on the new tenant's URL.
    activeSlugRef.current = urlSlug;
    setBrand(null);

    // No slug in URL → stay neutral (Dugsy default). Nothing else to do.
    if (!urlSlug) {
      setBootstrapped(true);
      return;
    }

    // STEP 2 — async fetch, guarded by both the local `cancelled` flag
    // (for React unmount) and `activeSlugRef` (for stale-response race).
    let cancelled = false;
    (async () => {
      const data = await fetchBrand(urlSlug);
      if (cancelled) return;
      // If another navigation superseded us mid-flight, discard.
      if (activeSlugRef.current !== urlSlug) return;
      setBrand(data || null);
      setBootstrapped(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [urlSlug, fetchBrand]);

  /**
   * Public: force-fetch a brand by slug. Used by SluggedLoginPage,
   * ClientLandingPage, RegisterPage on their imperative init paths.
   * NOTE: In practice these callers are already on a slug-URL so the
   * URL-driven effect above handles them. This method is kept for API
   * stability and returns the fetched brand doc.
   *
   * SLUG-IS-MASTER: if the caller's requested slug doesn't match the
   * current URL slug, we still fetch and set (imperative callers know
   * what they're doing) — but we DO NOT cache anywhere.
   */
  const setBrandBySlug = useCallback(
    async (slug) => {
      if (!slug) {
        activeSlugRef.current = null;
        setBrand(null);
        return null;
      }
      // Same synchronous-clear guarantee for imperative callers.
      activeSlugRef.current = slug;
      setBrand(null);
      const data = await fetchBrand(slug);
      // Only apply if this call is still the latest requested slug.
      if (activeSlugRef.current === slug) {
        setBrand(data || null);
      }
      return data;
    },
    [fetchBrand]
  );

  const clearBrand = useCallback(() => {
    activeSlugRef.current = null;
    setBrand(null);
  }, []);

  // Apply CSS variables to :root whenever the brand changes.
  useEffect(() => {
    const root = document.documentElement;
    const primary = brand?.primary_color || DEFAULT_PRIMARY;
    root.style.setProperty("--brand-primary", primary);
    root.style.setProperty("--brand-primary-hover", darkenHex(primary, 12));
    root.style.setProperty("--brand-bg-tint", hexToRgba(primary, 0.05));
  }, [brand]);

  const value = useMemo(
    () => ({ brand, bootstrapped, setBrandBySlug, clearBrand }),
    [brand, bootstrapped, setBrandBySlug, clearBrand]
  );

  return <BrandCtx.Provider value={value}>{children}</BrandCtx.Provider>;
}

export function useBrand() {
  return useContext(BrandCtx);
}
